import classNames from 'classnames';
import {
  type ButtonHTMLAttributes,
  createContext,
  forwardRef,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useContext,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Collapse } from '../collapse/Collapse';
import styles from './Accordion.module.css';

interface AccordionContextValue {
  openValues: string[];
  toggle: (value: string) => void;
}

const AccordionContext = createContext<AccordionContextValue | null>(null);

interface AccordionItemContextValue {
  open: boolean;
  toggle: () => void;
  buttonId: string;
  panelId: string;
}

const AccordionItemContext = createContext<AccordionItemContextValue | null>(
  null
);

const useAccordionContext = (component: string): AccordionContextValue => {
  const context = useContext(AccordionContext);
  if (!context) {
    throw new Error(`<${component}> must be used inside <Accordion>`);
  }
  return context;
};

const useAccordionItemContext = (
  component: string
): AccordionItemContextValue => {
  const context = useContext(AccordionItemContext);
  if (!context) {
    throw new Error(`<${component}> must be used inside <AccordionItem>`);
  }
  return context;
};

export interface AccordionProps {
  /** Lets several sections be open at once; otherwise opening one closes the rest. */
  allowMultiple?: boolean;
  /** The open sections' values, when the caller owns the open state. */
  value?: string[];
  defaultValue?: string[];
  onChange?: (value: string[]) => void;
  children: ReactNode;
  className?: string;
}

/**
 * Chakra's Accordion, value-based like `Tabs`: sections are named by their
 * `value`, not their position. Clicking an open section closes it.
 */
export const Accordion = ({
  allowMultiple = false,
  value: controlledValue,
  defaultValue = [],
  onChange,
  children,
  className,
}: AccordionProps) => {
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
  const isControlled = controlledValue !== undefined;
  const openValues = isControlled ? controlledValue : uncontrolledValue;
  const rootRef = useRef<HTMLDivElement>(null);

  const toggle = useCallback(
    (itemValue: string) => {
      let next: string[];
      if (openValues.includes(itemValue)) {
        next = openValues.filter((open) => open !== itemValue);
      } else if (allowMultiple) {
        next = [...openValues, itemValue];
      } else {
        next = [itemValue];
      }
      if (!isControlled) {
        setUncontrolledValue(next);
      }
      onChange?.(next);
    },
    [openValues, allowMultiple, isControlled, onChange]
  );

  // Up/Down/Home/End move focus between this accordion's own headers, as in
  // Chakra. Keys from anything else (a drag handle, a nested accordion) pass.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const root = rootRef.current;
    const target = event.target as HTMLElement;
    if (
      !root ||
      !target.hasAttribute('data-accordion-button') ||
      target.closest('[data-accordion]') !== root
    ) {
      return;
    }
    const buttons = Array.from(
      root.querySelectorAll<HTMLButtonElement>('[data-accordion-button]')
    ).filter((button) => button.closest('[data-accordion]') === root);
    const index = buttons.indexOf(target as HTMLButtonElement);
    let nextIndex: number;
    switch (event.key) {
      case 'ArrowDown':
        nextIndex = (index + 1) % buttons.length;
        break;
      case 'ArrowUp':
        nextIndex = (index - 1 + buttons.length) % buttons.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = buttons.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    buttons[nextIndex]?.focus();
  };

  const context = useMemo<AccordionContextValue>(
    () => ({ openValues, toggle }),
    [openValues, toggle]
  );

  return (
    <AccordionContext.Provider value={context}>
      <div
        ref={rootRef}
        className={className}
        data-accordion=""
        onKeyDown={handleKeyDown}
      >
        {children}
      </div>
    </AccordionContext.Provider>
  );
};

export interface AccordionItemProps extends HTMLAttributes<HTMLDivElement> {
  /** Names the section in the accordion's `value`; only needed when it is set or starts open. */
  value?: string;
}

export const AccordionItem = forwardRef<HTMLDivElement, AccordionItemProps>(
  function AccordionItem({ value, children, ...rest }, ref) {
    const { openValues, toggle } = useAccordionContext('AccordionItem');
    const id = useId();
    const itemValue = value ?? id;
    const open = openValues.includes(itemValue);

    const context = useMemo<AccordionItemContextValue>(
      () => ({
        open,
        toggle: () => toggle(itemValue),
        buttonId: `${id}-button`,
        panelId: `${id}-panel`,
      }),
      [open, toggle, itemValue, id]
    );

    return (
      <AccordionItemContext.Provider value={context}>
        <div ref={ref} {...rest}>
          {children}
        </div>
      </AccordionItemContext.Provider>
    );
  }
);

export type AccordionButtonProps = ButtonHTMLAttributes<HTMLButtonElement>;

/** The section's header. Its padding is `--accordion-button-padding`. */
export const AccordionButton = ({
  className,
  onClick,
  ...rest
}: AccordionButtonProps) => {
  const { open, toggle, buttonId, panelId } =
    useAccordionItemContext('AccordionButton');

  return (
    <button
      type="button"
      {...rest}
      id={buttonId}
      className={classNames(styles.button, className)}
      aria-expanded={open}
      aria-controls={panelId}
      data-accordion-button=""
      onClick={(event) => {
        onClick?.(event);
        toggle();
      }}
    />
  );
};

export interface AccordionPanelProps {
  children: ReactNode;
  className?: string;
}

/**
 * The section's content, kept mounted while closed (see `Collapse`). Its
 * padding is `--accordion-panel-padding`.
 */
export const AccordionPanel = ({
  children,
  className,
}: AccordionPanelProps) => {
  const { open, buttonId, panelId } = useAccordionItemContext('AccordionPanel');

  return (
    <Collapse id={panelId} role="region" aria-labelledby={buttonId} open={open}>
      <div className={classNames(styles.panel, className)}>{children}</div>
    </Collapse>
  );
};

/** Chakra's chevron, turned over while its section is open. */
export const AccordionIcon = ({ className }: { className?: string }) => {
  const { open } = useAccordionItemContext('AccordionIcon');

  return (
    <svg
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      focusable="false"
      aria-hidden="true"
      className={classNames(styles.icon, className)}
      data-open={open || undefined}
    >
      <path
        fill="currentColor"
        d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z"
      />
    </svg>
  );
};
